import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  CheckCircle,
  ChevronRight,
  Calculator,
  Target
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';
import GridScaffold from '../../components/GridScaffold';

// EXACT color palette from LandingPage.jsx and Dashboard.jsx
const colors = {
  // Primary accent - deeper blue for a more formal, enterprise feel
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  // Neutral tones
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#9e9e9e',
  gray700: '#636363',
  gray900: '#333333',
  
  // Status colors (more subtle)
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

// EXACT font styles from LandingPage.jsx and Dashboard.jsx
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600, // Semi-bold for headings
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400, // Regular for body text
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500, // Medium weight
  },
  light: {
    fontWeight: 300, // Light weight
  }
};




// FAQ data aligned with new pricing structure
const faqData = [
  {
    question: "What's included in the $10/month Signature plan?",
    answer: "Full e-signature capabilities including our 8-second smart signing, auto-fill AI that reads and fills documents intelligently, legally binding signatures in 180+ countries, instant audit trails, unlimited documents, and up to 5 team members. Perfect for teams that need fast, reliable signatures without the contract creation complexity."
  },
  {
    question: "How is SignPact different from DocuSign?",
    answer: "Our Signature tier is 5x faster (8 seconds vs 2+ minutes), 80% cheaper, and includes smart auto-fill AI that actually understands your documents. Our Business and Enterprise tiers go beyond signatures to include AI contract creation, risk analysis, and full lifecycle management - something DocuSign doesn't offer."
  },
  {
    question: "When should I upgrade from Signature to Business?",
    answer: "If you're creating contracts from scratch, need risk analysis, want AI-powered templates, or handle more than 10 contracts monthly, the Business tier will save you 70% in time and costs with full contract automation. The Business tier includes everything in Signature plus AI contract assembly with 500+ templates."
  },
  {
    question: "What makes the Enterprise tier industry-specific?",
    answer: "We've trained our AI on thousands of contracts from your specific industry. It knows your terminology, compliance requirements, and best practices. For example, our SaaS AI knows to include GDPR compliance and SLA terms automatically, while our healthcare AI ensures HIPAA compliance in every contract."
  },
  {
    question: "Can I change or cancel my plan later?",
    answer: "Absolutely! Start with our $10 Signature tier and upgrade as you grow. Your data, templates, and settings transfer seamlessly, and you only pay the difference prorated for that month. No long-term contracts, cancel anytime."
  }
];

const WorkflowReplacementDemo = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isYearlyPricing, setIsYearlyPricing] = useState(true); // Default to yearly for better price
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };



  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col overflow-hidden" 
         style={{
           color: colors.gray900,
           fontFamily: fontStyles.fontFamily
         }}>
      
      <NavBar activePage="pricing" />
      


      


      {/* Pricing Tiers - Premium Design Language */}
      <motion.div className="py-20 relative" style={{backgroundColor: colors.gray50}}>
        <GridScaffold
  containerMax="max-w-6xl"
  containerPx={24}
  railColor="#1e40af"
  railOpacity={0.25}
  railWidth={1.5}
  railStyle="solid"
  railDouble
  railDoubleGap={17}
  showSecondaryGrid={false}
  gridGap={120}
  gridColor="#1e40af"
  gridOpacity={0.08}
  showRows={false}
  rowStep={120}
  rowColor="#1e40af"
  rowOpacity={0.06}
  showDotOverlay={false}
  showHatchGutters={true}
  hatchAngleDeg={135}
  hatchGap={32}
  hatchOpacity={0.11}
  hideOnMobile={false}
/>
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
          >
                           
            <h2 className="text-4xl md:text-5xl font-bold mb-8 mt-12" 
                style={{
                  ...fontStyles.heading,
                  letterSpacing: '-0.04em',
                  color: colors.gray900
                }}>
            Choose the perfect plan for your team
          </h2>
          <p className="text-xl max-w-3xl mx-auto mb-8" 
             style={{
               color: colors.gray700, 
               lineHeight: '1.6', 
               ...fontStyles.body
             }}>
            From lightning-fast e-signatures to AI-powered contract automation
          </p>
          
          {/* Pricing Toggle - banded to touch rails */}
          <div 
            className="relative z-10 mx-auto w-full"
            style={{
              position: 'relative',
              left: 'calc(-7px )',
              width: 'calc(100% + (7px) * 2)',
              borderTop: '1.5px dotted rgba(30, 64, 175, 0.25)',
              borderBottom: '1.5px dotted rgba(30, 64, 175, 0.25)',
              background: colors.accentLight
            }}
          >
            <div style={{ paddingLeft: 24, paddingRight: 24 }}>
              <div className="flex items-center justify-center gap-4 py-4">
                <span className={`text-sm font-medium ${!isYearlyPricing ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
                <button 
                  onClick={() => setIsYearlyPricing(!isYearlyPricing)}
                  className="relative inline-flex h-8 w-14 items-center rounded-full transition-colors"
                  style={{backgroundColor: isYearlyPricing ? colors.accent : colors.gray300}}
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${isYearlyPricing ? 'translate-x-7' : 'translate-x-1'}`}/>
                </button>
                <span className={`text-sm font-medium ${isYearlyPricing ? 'text-gray-900' : 'text-gray-500'}`}>
                  Yearly
                  <span className="ml-2 px-2 py-1 rounded-full text-xs" style={{backgroundColor: colors.successLight, color: colors.success}}>
                    Save 20%
                  </span>
                </span>
              </div>
            </div>
          </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Tier 1: Signature */}
              <motion.div
              className="p-6 relative overflow-hidden group w-full max-w-[380px] mx-auto"
                style={{
                borderRadius: '24px',
                border: `1px solid ${colors.gray200}`,
                background: `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)`,
                backdropFilter: 'blur(20px) saturate(180%)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.07)';
                e.currentTarget.style.borderColor = colors.accent + '40';
                e.currentTarget.style.filter = 'brightness(1.01)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = colors.gray200;
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              {/* Top Gradient Accent Strip */}
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background: 'linear-gradient(to right, rgba(30, 64, 175, 0.25), rgba(30, 64, 175, 0.5))'
                }}
              />
              {/* Spotlight hover overlay */}
              <div 
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(600px circle at 50% 0%, rgba(30, 64, 175, 0.08), transparent 40%)'
                }}
              />
              {/* Early Access Badge */}
              <div className="absolute top-4 right-4">
                <span className="px-4 py-1.5 rounded-full text-xs font-semibold" 
                      style={{
                        background: `linear-gradient(135deg, ${colors.successLight} 0%, rgba(52, 199, 89, 0.08) 100%)`,
                        border: `1px solid ${colors.success}30`,
                        color: colors.success,
                        ...fontStyles.medium
                      }}>
                  FIRST 100 CUSTOMERS
                </span>
              </div>
              
                {/* Header */}
              <div className="mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                       style={{
                       backgroundColor: colors.accent + '20',
                       border: `2px solid ${colors.accent}40`
                     }}>
                  <Zap size={24} style={{color: colors.accent}} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{...fontStyles.heading, color: colors.gray900}}>
                  For Freelancers & Early Startups
                </h3>
                <p className="text-sm mb-4" style={{color: colors.gray700, ...fontStyles.body}}>
                  World's fastest e-signature workflow - 8 seconds from open to signed
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold" style={{color: colors.accent, ...fontStyles.heading}}>
                    ${isYearlyPricing ? '10' : '15'}
                  </span>
                  <span className="text-sm" style={{color: colors.gray600}}>/month</span>
                  {isYearlyPricing && (
                    <span className="text-xs ml-2" style={{color: colors.gray500}}>billed yearly</span>
                  )}
                </div>
              </div>
              
              {/* Features */}
              <div className="mb-4 pb-4 border-b" style={{borderColor: colors.gray200}}>
                <ul className="space-y-2">
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Instant e-signature access</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Smart auto-fill (8-second signing)</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Legally binding in 180+ countries</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Real-time audit trail</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Up to 5 team members</span>
                  </li>
                </ul>
                  </div>
              
              {/* Perfect For */}
              <div className="mb-4">
                <div className="text-xs font-medium mb-2" style={{color: colors.gray500}}>PERFECT FOR</div>
                <div className="text-sm" style={{color: colors.gray700}}>
                  Freelancers, small teams, startups needing just signatures
                    </div>
                  </div>
              
              {/* Highlight */}
              <div className="p-3 rounded-xl mb-4" 
                   style={{
                     background: `linear-gradient(135deg, ${colors.accentLight} 0%, rgba(30, 64, 175, 0.05) 100%)`,
                     border: `1px solid ${colors.accent}20`,
                     backdropFilter: 'blur(10px)'
                   }}>
                <div className="text-sm font-semibold" style={{color: colors.accent, ...fontStyles.medium}}>
                  40% faster than DocuSign at 1/5 the cost
                </div>
              </div>
              
              {/* CTA */}
              <button 
                onClick={() => navigate('/signup')}
                className="w-full py-2.5 text-white font-semibold transition-all duration-300 relative overflow-hidden group"
                style={{
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                  boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                }}
              >
                Start Free Trial
              </button>
            </motion.div>

            {/* Tier 2: AI Contract Generation */}
            <motion.div
              className="p-6 relative overflow-hidden group w-full max-w-[380px] mx-auto"
              style={{
                borderRadius: '24px',
                border: `2px solid ${colors.accent}30`,
                background: `linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(30, 64, 175, 0.03) 100%)`,
                backdropFilter: 'blur(20px) saturate(180%)',
                boxShadow: '0 12px 40px rgba(30, 64, 175, 0.15), 0 8px 20px rgba(0, 0, 0, 0.08)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                transform: 'scale(1.02)'
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.6, -0.05, 0.01, 0.99] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.03)';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(30, 64, 175, 0.2), 0 12px 25px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = colors.accent + '50';
                e.currentTarget.style.filter = 'brightness(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(30, 64, 175, 0.15), 0 8px 20px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = colors.accent + '30';
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              {/* Top Gradient Accent Strip */}
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background: 'linear-gradient(to right, rgba(30, 64, 175, 0.25), rgba(30, 64, 175, 0.5))'
                }}
              />
              {/* Spotlight hover overlay */}
              <div 
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(600px circle at 50% 0%, rgba(30, 64, 175, 0.10), transparent 40%)'
                }}
              />
                            {/* Most Popular Badge */}
              <div className="absolute top-4 right-4">
                <span className="px-4 py-1.5 rounded-full text-xs font-semibold text-white" 
                      style={{
                        background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                        boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                        ...fontStyles.medium
                      }}>
                  MOST POPULAR
                </span>
                </div>
                
              {/* Header */}
              <div className="mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                     style={{
                       backgroundColor: colors.accent,
                       boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)'
                     }}>
                  <Target size={24} style={{color: colors.white}} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{...fontStyles.heading, color: colors.gray900}}>
                  AI Contract Generation
                </h3>
                <p className="text-sm mb-4" style={{color: colors.gray700, ...fontStyles.body}}>
                  Create, analyze, and manage contracts with advanced AI
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold" style={{color: colors.gray900, ...fontStyles.heading}}>
                    Custom Pricing
                  </span>
                </div>
              </div>
              
              {/* Features */}
              <div className="mb-4 pb-4 border-b" style={{borderColor: colors.gray200}}>
                <ul className="space-y-2">
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}><strong>Everything in Signature</strong></span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>AI contract generation (50+ templates)</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Industry-specific legal language AI</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Automated risk analysis & compliance</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Contract lifecycle management</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle size={16} style={{color: colors.success}} className="mr-3 mt-0.5 flex-shrink-0" />
                    <span style={{color: colors.gray700}}>Unlimited team members</span>
                  </li>
                </ul>
              </div>
              
              {/* Perfect For */}
              <div className="mb-4">
                <div className="text-xs font-medium mb-2" style={{color: colors.gray500}}>PERFECT FOR</div>
                <div className="text-sm" style={{color: colors.gray700}}>
                  Teams creating 20+ contracts/month, enterprises, legal departments
                </div>
              </div>
              
              {/* Highlight */}
                <div className="p-3 rounded-xl mb-4"
                   style={{
                     background: `linear-gradient(135deg, ${colors.successLight} 0%, rgba(52, 199, 89, 0.05) 100%)`,
                     border: `1px solid ${colors.success}20`,
                     backdropFilter: 'blur(10px)'
                   }}>
                <div className="text-sm font-semibold" style={{color: colors.success, ...fontStyles.medium}}>
                  Generate contracts in 2 minutes vs 2 hours
                </div>
              </div>
              
              {/* CTA */}
              <button 
                onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank', 'noopener,noreferrer')}
                className="w-full py-2.5 text-white font-semibold transition-all duration-300 relative overflow-hidden group"
                style={{
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                  boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 15px 40px rgba(30, 64, 175, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  e.target.style.filter = 'brightness(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 10px 30px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)';
                  e.target.style.filter = 'brightness(1)';
                }}
              >
                Book a Demo
              </button>
            </motion.div>

          </div>
          {/* Feature Comparison Link */}
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <button 
              onClick={() => navigate('/roi-calculator')}
              className="inline-flex items-center text-sm font-medium transition-colors"
              style={{color: colors.accent, ...fontStyles.medium}}
              onMouseEnter={(e) => e.target.style.color = colors.accentHover}
              onMouseLeave={(e) => e.target.style.color = colors.accent}
            >
              <Calculator size={16} className="mr-2" />
              Calculate your ROI with SignPact
            </button>
          </motion.div>
        </div>
      </motion.div>

      {/* Section Divider - Double Rail */}
      <div className="relative w-full">
        <div className="w-full" style={{ borderTop: '1.5px solid rgba(30, 64, 175, 0.25)' }} />
        <div className="w-full" style={{ height: '8px', background: 'transparent' }} />
        <div className="w-full" style={{ borderTop: '1.5px solid rgba(30, 64, 175, 0.25)' }} />
      </div>

      {/* FAQ Section - Premium Design Language */}
      <motion.div 
        className="py-20 relative"
        style={{background: `linear-gradient(to bottom, ${colors.white}, ${colors.gray50})`}}
      >
        <GridScaffold
          containerMax="max-w-6xl"
          containerPx={24}
          railColor="#1e40af"
          railOpacity={0.25}
          railWidth={1.5}
          railStyle="solid"
          railDouble
          railDoubleGap={17}
          showSecondaryGrid={false}
          gridGap={120}
          gridColor="#1e40af"
          gridOpacity={0.08}
          showRows={false}
          rowStep={120}
          rowColor="#1e40af"
          rowOpacity={0.06}
          showDotOverlay={false}
          showHatchGutters={true}
          hatchAngleDeg={135}
          hatchGap={32}
          hatchOpacity={0.11}
          hideOnMobile={false}
          showRightRails={true}
          showRightHatchGutter={true}
        />
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            {/* Left: Heading + copy + CTA */}
            <motion.div 
              className="md:col-span-5 md:col-start-2 md:pr-6 text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
            >
              <motion.div 
                className="inline-block px-5 py-2 rounded-full text-sm font-medium mb-6" 
                style={{
                  background: `linear-gradient(135deg, ${colors.accentLight} 0%, rgba(30, 64, 175, 0.08) 100%)`,
                  border: `1px solid ${colors.accent}20`,
                  color: colors.accent,
                  ...fontStyles.medium
                }}
                whileHover={{ scale: 1.02 }}
              >
                Frequently Asked Questions
              </motion.div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" 
                  style={{
                    ...fontStyles.heading,
                    letterSpacing: '-0.04em',
                    color: colors.gray900
                  }}>
                Everything you need to know
              </h2>
              <p className="text-xl mb-8" 
                 style={{
                   color: colors.gray700,
                   lineHeight: '1.6',
                   ...fontStyles.body
                 }}>
                Clear answers to common questions about our pricing and features
              </p>
              <button 
                onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank', 'noopener,noreferrer')}
                className="px-5 py-2.5 text-white font-semibold transition-all"
                style={{
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                  boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25)'
                }}
              >
                Book a Demo
              </button>
            </motion.div>

            {/* Right: Accordion */}
            <div className="space-y-5 md:col-span-6 md:col-start-7">
              {faqData.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.6, -0.05, 0.01, 0.99] }}
                  className="overflow-hidden"
                  style={{
                    borderRadius: '24px',
                    border: `1px solid ${colors.gray200}`,
                    background: `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)`,
                    backdropFilter: 'blur(20px) saturate(180%)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.07)';
                    e.currentTarget.style.borderColor = colors.accent + '30';
                    e.currentTarget.style.filter = 'brightness(1.01)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.borderColor = colors.gray200;
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  <motion.button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left p-8 flex justify-between items-center"
                    style={{
                      color: activeFaq === index ? colors.accent : colors.gray900,
                      ...fontStyles.heading
                    }}
                    whileHover={{ color: colors.accent }}
                  >
                    <h3 className="text-xl font-bold pr-4">{faq.question}</h3>
                    <motion.div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: activeFaq === index 
                          ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)` 
                          : `linear-gradient(135deg, ${colors.gray100} 0%, ${colors.gray50} 100%)`,
                        boxShadow: activeFaq === index 
                          ? '0 4px 12px rgba(30, 64, 175, 0.2)' 
                          : '0 2px 6px rgba(0, 0, 0, 0.05)'
                      }}
                      animate={{ 
                        rotate: activeFaq === index ? 90 : 0,
                        scale: activeFaq === index ? 1.1 : 1
                      }}
                      transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
                    >
                      <ChevronRight 
                        size={18} 
                        style={{
                          color: activeFaq === index ? colors.white : colors.gray500
                        }}
                      />
                    </motion.div>
                  </motion.button>
                  
                  <AnimatePresence>
                    {activeFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-8">
                          <p className="text-lg leading-relaxed" style={{color: colors.gray700, lineHeight: '1.7', ...fontStyles.body}}>
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Section Divider - Double Rail (end of FAQ) */}
      <div className="relative w-full">
        <div className="w-full" style={{ borderTop: '1.5px solid rgba(30, 64, 175, 0.25)' }} />
        <div className="w-full" style={{ height: '8px', background: 'transparent' }} />
        <div className="w-full" style={{ borderTop: '1.5px solid rgba(30, 64, 175, 0.25)' }} />
      </div>

      <ResponsiveFooter />
    </div>
  );
};

export default WorkflowReplacementDemo; 